export const TEST_CASE_TYPES = [
  'FUNCTIONAL',
  'NON_FUNCTIONAL',
  'SMOKE',
  'REGRESSION',
  'INTEGRATION',
  'NEGATIVE',
  'EXPLORATORY',
] as const;
export type TestCaseType = (typeof TEST_CASE_TYPES)[number];

export const TEST_CASE_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export type TestCasePriority = (typeof TEST_CASE_PRIORITIES)[number];

export const TEST_TYPES = [
  'UNIT',
  'INTEGRATION',
  'SYSTEM',
  'ACCEPTANCE',
  'REGRESSION',
  'SMOKE',
  'PERFORMANCE',
] as const;
export type TestType = (typeof TEST_TYPES)[number];

export const TEST_CASE_STATUSES = ['DRAFT', 'READY', 'ACTIVE', 'DEPRECATED'] as const;
export type TestCaseStatus = (typeof TEST_CASE_STATUSES)[number];

export type AutomationStatus = 'MANUAL' | 'AUTOMATABLE' | 'AUTOMATED';

export const TEST_CASE_AUTOMATION_FRAMEWORKS = [
  'PLAYWRIGHT',
  'SELENIUM',
  'CYPRESS',
  'ROBOT_FRAMEWORK',
  'POSTMAN_NEWMAN',
] as const;
export type TestCaseAutomationFramework = (typeof TEST_CASE_AUTOMATION_FRAMEWORKS)[number];
export type TestExecutionStatus = 'PASSED' | 'FAILED' | 'BLOCKED' | 'RUNNING' | 'SKIPPED' | 'NOT_RUN';

export interface ExecutionStats {
  passed: number;
  failed: number;
  blocked: number;
  running: number;
  skipped: number;
  notRun: number;
  /** Bestanden / alle Ausführungen, als ganzzahliger Prozentwert (0–100). */
  passRate: number;
}

/**
 * Projektweites Kopfbereich-Aggregat der Testfall-Übersicht (NEX-405): zählt je
 * Testfall den letzten Ausführungsstatus, nicht jede einzelne Ausführung.
 */
export interface ExecutionSummary {
  total: number;
  passed: number;
  failed: number;
  /** Bestanden / alle Testfälle, als ganzzahliger Prozentwert (0–100). */
  passRate: number;
}

export type TestStepKind = 'ACTION' | 'MODULE_REF';

/** Herkunft eines Schritts in der projektweiten Übersicht (NEX-592). */
export type TestStepOrigin = 'LIBRARY' | 'TESTCASE';

export type TestStepRunStatus = 'DRAFT' | 'RUNNING' | 'PASSED' | 'FAILED';

/** Rolle eines Bibliotheks-Schritts; steuert das Editor-Layout (NEX-456). */
export const STEP_ROLES = ['PRECONDITION', 'STEP', 'POSTCONDITION'] as const;
export type StepRole = (typeof STEP_ROLES)[number];

/** Frei vergebbare Kategorie eines Bibliotheks-Schritts; treibt Listen-Pill und Filter. */
export const STEP_CATEGORIES = ['SETUP', 'AUTHENTICATION', 'VERIFICATION', 'TEARDOWN'] as const;
export type StepCategory = (typeof STEP_CATEGORIES)[number];

export interface TestStep {
  id: string;
  /** Menschenlesbarer, projektweit laufender Key (z. B. 'TS-018', NEX-654). In ID-Pill statt UUID angezeigt. */
  key?: string | null;
  testCaseId?: string | null;
  testModuleOwnerId?: string | null;
  /** Gesetzt bei projektweiten Bibliotheks-Schritten (NEX-456). */
  projectId?: string | null;
  title?: string | null;
  kind: TestStepKind;
  orderIndex: number;
  action?: string | null;
  expectedResult?: string | null;
  notes?: string | null;
  /** Automatisierungsanweisung (Classic-TCDL); null bedeutet manueller Schritt. */
  tcdl?: string | null;
  status: TestStepRunStatus;
  stepRole?: StepRole;
  category?: StepCategory | null;
  /** Verantwortlicher des Bibliotheks-Schritts (NEX-456), analog TestCase.assigneeUserId. */
  assigneeUserId?: string | null;
  /** Anzeigename des Verantwortlichen, serverseitig aus assigneeUserId aufgelöst. */
  assigneeName?: string | null;
  /** Anzahl Testfälle, die den Bibliotheks-Schritt verwenden (heute statisch 0). */
  usageCount?: number;
  /** Frei vergebbare Tags des Bibliotheks-Schritts (NEX-459). */
  tags?: string[];
  /** Verknüpfter Story-Jira-Key (NEX-459); null = keiner. */
  storyJiraKey?: string | null;
  /** Verknüpfter Bug-Jira-Key (NEX-459); null = keiner. */
  bugJiraKey?: string | null;
  calledModuleId?: string | null;
  /**
   * NEX-1004/1051: verbundenes Testdatenblatt und dessen Zeilen-Selektoren am
   * MODULE_REF-Schritt. Filter-Eintraege in der BE-Form (`{type,expr}` bzw.
   * `{type,variable,value}`) — die UI arbeitet mit einem Freetext-Feld und
   * uebersetzt beim Save (siehe `data-sheet-filters.codec`).
   */
  dataSheetId?: string | null;
  dataSheetFilters?: Record<string, string>[] | null;
  /**
   * NEX-1053: Bedingung am MODULE_REF-Schritt (`{type:VALUE_COMPARE,operand,operator,value}`
   * bzw. Objekt-/Fensterbedingungen). Objektbedingungen editiert die UI nicht, laesst
   * sie aber im Save-Diff stehen.
   */
  stepCondition?: Record<string, string> | null;
  /**
   * FE-lokaler Verknüpfungs-Marker (NEX-546): auf einer unsaved Referenz-Zeile
   * die Bibliotheks-Step-ID, aus der der Mapper beim Save die testStepId ableitet.
   * Rein transient — der Server befüllt dieses Feld nie.
   */
  libraryStepId?: string | null;
  /** Herkunft in der projektweiten Übersicht (NEX-592). */
  origin?: TestStepOrigin;
  /** Titel des Testfalls, falls origin=TESTCASE. */
  testCaseTitle?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Ein Verwendungs-Eintrag eines Bibliotheks-Schritts (Modul oder Testfall), NEX-463. */
export interface TestStepUsageRef {
  id: string;
  displayId: string;
  title: string;
}

/** Verwendung eines Bibliotheks-Schritts (NEX-463): einbindende Module + Testfälle. */
export interface TestStepUsage {
  modules: TestStepUsageRef[];
  testCases: TestStepUsageRef[];
}

export interface CreateTestStepRequest {
  kind: TestStepKind;
  orderIndex?: number | null;
  action?: string | null;
  expectedResult?: string | null;
  notes?: string | null;
  status?: TestStepRunStatus | null;
  calledModuleId?: string | null;
  title?: string | null;
  stepRole?: StepRole | null;
  category?: StepCategory | null;
  assigneeUserId?: string | null;
  tags?: string[];
  storyJiraKey?: string | null;
  bugJiraKey?: string | null;
  tcdl?: string | null;
}

export interface UpdateTestStepRequest {
  orderIndex?: number | null;
  action?: string | null;
  expectedResult?: string | null;
  notes?: string | null;
  status?: TestStepRunStatus | null;
  title?: string | null;
  stepRole?: StepRole | null;
  category?: StepCategory | null;
  assigneeUserId?: string | null;
  clearAssigneeUserId?: boolean;
  tags?: string[];
  storyJiraKey?: string | null;
  bugJiraKey?: string | null;
  /** Leerer String loescht die Anweisung; null laesst sie unveraendert. */
  tcdl?: string | null;
}

/**
 * Buffered-Save-Request für den Bibliotheks-Schritt (NEX-600/NEX-612):
 * ein einziger POST/PUT ersetzt granulare Feld-Requests. Update-Semantik
 * entspricht dem BE: {@code null} lässt das Feld unverändert, ein leerer
 * String setzt es aktiv zurück. Für {@code assigneeUserId} gilt zusätzlich
 * Full-Replace: {@code null} entfernt den Verantwortlichen.
 */
export interface SaveFullTestStepRequest {
  title?: string | null;
  action?: string | null;
  expectedResult?: string | null;
  notes?: string | null;
  /** Classic-TCDL-Skript (NEX-1044); leerer String löscht, null lässt unverändert. */
  tcdl?: string | null;
  status?: TestStepRunStatus | null;
  stepRole?: StepRole | null;
  category?: StepCategory | null;
  tags?: string[];
  storyJiraKey?: string | null;
  bugJiraKey?: string | null;
  assigneeUserId?: string | null;
}

/**
 * Response zum {@link SaveFullTestStepRequest}. {@code clientIdMapping} ist heute
 * bewusst leer (keine Kind-Kollektionen), bleibt aber Teil des Contracts, damit
 * der Endpoint spaeter ohne Bruch erweitert werden kann.
 */
export interface SaveFullTestStepResponse {
  testStep: TestStep;
  clientIdMapping: Record<string, string>;
}

/** Aktivitätstyp im Audit-Feed eines Bibliotheks-Schritts (NEX-459). */
export type TestStepActivityType = 'CREATED' | 'EDITED';

/** Audit-Eintrag im Aktivitäts-Feed eines Bibliotheks-Schritts (NEX-459). */
export interface TestStepActivity {
  id: string;
  testStepId: string;
  type: TestStepActivityType;
  /** Typ-spezifische Details, serverseitig als jsonb. */
  payload?: Record<string, unknown> | null;
  createdBy: string;
  /** Anzeigename des Akteurs, serverseitig aus der Tenant-User-Directory aufgeloest (NEX-743). Fehlt, wenn der Nutzer nicht (mehr) auffindbar ist. */
  createdByName?: string | null;
  createdAt: string;
}

/** Datei-Anhang eines Bibliotheks-Schritts (NEX-459). */
export interface TestStepAttachment {
  id: string;
  testStepId: string;
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
  createdBy: string;
  createdAt: string;
}

export interface TestCase {
  id: string;
  /** Menschenlesbarer, projektweit laufender Key (z. B. 'TC-018', NEX-408). In ID-Pill statt UUID angezeigt. */
  key: string;
  projectId: string;
  spaceId?: string | null;
  title: string;
  description?: string | null;
  preconditions?: string | null;
  postconditions?: string | null;
  type: TestCaseType;
  testType: TestType;
  priority: TestCasePriority;
  status: TestCaseStatus;
  version: number;
  createdAt: string;
  updatedAt: string;
  testGoal?: string | null;
  tags: string[];
  requirementId?: string | null;
  requirementLabel?: string | null;
  storyJiraKey?: string | null;
  storyTitleSnapshot?: string | null;
  storyDescriptionSnapshot?: string | null;
  storySyncedAt?: string | null;
  bugJiraKey?: string | null;
  bugTitleSnapshot?: string | null;
  bugDescriptionSnapshot?: string | null;
  bugSyncedAt?: string | null;
  notes?: string | null;
  assigneeUserId?: string | null;
  automationKey?: string | null;
  automationStatus?: AutomationStatus;
  automationFramework?: TestCaseAutomationFramework | null;
  automationLocator?: string | null;
  /** Letzter Ausführungsstatus (NEX-405); null = noch nie ausgeführt. Nur in der Listenansicht befüllt. */
  lastExecutionStatus?: TestExecutionStatus | null;
  steps?: TestStep[] | null;
  /** Anzeigename des Autors/Verantwortlichen, aufgelöst aus assigneeUserId (NEX-406). Nur in der Listenansicht befüllt. */
  authorName?: string | null;
  /** Anzahl aktiver Testschritte (NEX-406). Nur in der Listenansicht befüllt. */
  stepCount?: number | null;
  /** Herkunft aus einem übernommenen KI-Entwurf (NEX-1536); beide null bei manuell angelegten Testfällen. */
  kiEntwurfId?: string | null;
  kiEntwurfAm?: string | null;
}

export interface TestCaseRevision {
  id: string;
  testCaseId: string;
  version: number;
  title: string;
  description?: string | null;
  testGoal?: string | null;
  preconditions?: string | null;
  assigneeUserId?: string | null;
  createdAt: string;
  createdBy: string;
}

export const TEST_CASE_ACTIVITY_TYPES = [
  'CREATED',
  'EDITED',
  'LINKED',
  'UNLINKED',
  'STEP_ADDED',
  'STEP_PASSED',
  'STEP_FAILED',
  'COMMENT',
  'HANDED_OVER',
  'HANDOVER_CLOSED',
  'HANDOVER_REOPENED',
] as const;
export type TestCaseActivityType = (typeof TEST_CASE_ACTIVITY_TYPES)[number];

/** Audit-Eintrag im Aktivitäts-Feed eines Testfalls (NEX-392). */
export interface TestCaseActivity {
  id: string;
  testCaseId: string;
  type: TestCaseActivityType;
  /** Typ-spezifische Details (z. B. Referenz einer Verknüpfung), serverseitig als jsonb. */
  payload?: Record<string, unknown> | null;
  createdBy: string;
  /** Anzeigename des Akteurs, serverseitig aus der Tenant-User-Directory aufgeloest (NEX-743). Fehlt, wenn der Nutzer nicht (mehr) auffindbar ist. */
  createdByName?: string | null;
  createdAt: string;
}

export interface CreateTestCaseRequest {
  title: string;
  description?: string | null;
  preconditions?: string | null;
  postconditions?: string | null;
  testGoal?: string | null;
  type?: TestCaseType;
  testType?: TestType;
  priority?: TestCasePriority;
  status?: TestCaseStatus;
  tags?: string[];
  requirementId?: string | null;
  storyJiraKey?: string | null;
  bugJiraKey?: string | null;
  notes?: string | null;
  assigneeUserId?: string | null;
  automationStatus?: AutomationStatus;
  automationFramework?: TestCaseAutomationFramework | null;
  automationLocator?: string | null;
}

export type CoverageKind = 'DIRECT' | 'INDIRECT';

export interface TestStepDraft {
  id?: string | null;
  clientId?: string | null;
  /**
   * UUID der bestehenden projektweiten TestStep-Definition, die verknuepft werden soll (NEX-546).
   * Gesetzt fuer aus der Bibliothek eingebundene Zeilen; null fuer neu getippte Schritte. Das BE
   * verknuepft (kopiert nicht) und wendet die mitgelieferten Inhaltsfelder auf die geteilte
   * Definition an — Editieren wirkt so ueberall.
   */
  testStepId?: string | null;
  kind: TestStepKind;
  title?: string | null;
  action?: string | null;
  expectedResult?: string | null;
  notes?: string | null;
  /** Classic-TCDL-Skript (NEX-1044); null lässt unverändert, leerer String löscht. */
  tcdl?: string | null;
  status?: TestStepRunStatus | null;
  calledModuleId?: string | null;
  stepRole?: StepRole | null;
  category?: StepCategory | null;
  assigneeUserId?: string | null;
  tags?: string[];
  /** NEX-1004/1051: Testdatenblatt am Modulaufruf; `null` loescht die Zuordnung. */
  dataSheetId?: string | null;
  /** NEX-1004/1051: Zeilen-Selektoren, in BE-Form. `null`/leer = alle Zeilen. */
  dataSheetFilters?: Record<string, string>[] | null;
  /** NEX-1053: Bedingung; `null` entfernt sie. */
  stepCondition?: Record<string, string> | null;
}

export interface RequirementCoverageDraft {
  requirementId: string;
  coverageKind?: CoverageKind | null;
}

export interface SaveFullTestCaseRequest {
  title: string;
  description?: string | null;
  preconditions?: string | null;
  postconditions?: string | null;
  type?: TestCaseType;
  testType?: TestType;
  priority?: TestCasePriority;
  status?: TestCaseStatus;
  spaceId?: string | null;
  testGoal?: string | null;
  tags?: string[];
  requirementId?: string | null;
  storyJiraKey?: string | null;
  bugJiraKey?: string | null;
  notes?: string | null;
  assigneeUserId?: string | null;
  automationStatus?: AutomationStatus;
  automationFramework?: TestCaseAutomationFramework | null;
  automationLocator?: string | null;
  steps: TestStepDraft[];
  requirementCoverage: RequirementCoverageDraft[] | null;
}

export interface CoverageLink {
  requirementId: string;
  coverageKind?: CoverageKind | null;
}

export interface SaveFullTestCaseResponse {
  testCase: TestCase;
  steps: TestStep[];
  requirementCoverage: CoverageLink[];
  /** Mapping der beim Request mitgeschickten Client-IDs auf die vergebenen finalen UUIDs. */
  clientIdMapping: Record<string, string>;
}

export interface UpdateTestCaseRequest {
  title?: string;
  description?: string | null;
  preconditions?: string | null;
  postconditions?: string | null;
  testGoal?: string | null;
  type?: TestCaseType;
  testType?: TestType;
  priority?: TestCasePriority;
  status?: TestCaseStatus;
  spaceId?: string | null;
  tags?: string[];
  requirementId?: string | null;
  clearRequirementId?: boolean;
  storyJiraKey?: string | null;
  clearStoryJiraKey?: boolean;
  bugJiraKey?: string | null;
  clearBugJiraKey?: boolean;
  notes?: string | null;
  assigneeUserId?: string | null;
  clearAssigneeUserId?: boolean;
  automationStatus?: AutomationStatus;
  automationFramework?: TestCaseAutomationFramework | null;
  clearAutomationFramework?: boolean;
  automationLocator?: string | null;
  clearAutomationLocator?: boolean;
}
